import type {
  CasePropertiesValue,
  CaseTemplateVersionAttributes,
  EntityRefProperty,
  EventRefProperty,
} from "src/clients/case-management";
import type { WorkspaceFeatureGates } from "src/clients/flow-api";

export type TransformContext = {
  sourceWorkspaceId: string;
  targetWorkspaceId: string;
  targetFeatureGates: WorkspaceFeatureGates;
};

export const isEntityRefProperty = (
  prop: CasePropertiesValue,
): prop is { type: "entity_ref" } & EntityRefProperty =>
  prop.type === "entity_ref";

export const isEventRefProperty = (
  prop: CasePropertiesValue,
): prop is { type: "event_ref" } & EventRefProperty =>
  prop.type === "event_ref";

/**
 * Filter case properties based on target workspace feature gates
 * - Remove entity properties if entity feature gate is disabled
 * - Remove event properties if feature (events) feature gate is disabled
 */
const filterCasePropertiesByFeatureGates = (
  properties: Record<string, CasePropertiesValue> = {},
  featureGates: WorkspaceFeatureGates,
): Record<string, CasePropertiesValue> =>
  Object.fromEntries(
    Object.entries(properties).filter(([, property]) => {
      if (isEntityRefProperty(property)) return featureGates.entity;
      if (isEventRefProperty(property)) return featureGates.feature;
      return true;
    }),
  );

/** System-generated fields that should not be copied to the new template version */
const SYSTEM_FIELDS: (keyof CaseTemplateVersionAttributes)[] = [
  "etag",
  "schema_version",
  "created_at",
  "updated_at",
  "published_at",
];

/**
 * Transform template version based on workspace context:
 * - Copies all attributes by default (future-proof for new fields)
 * - Removes system-generated fields
 * - Same workspace → all values preserved except publish state
 * - Cross-workspace → filter entity/event properties by feature gates, preserve flow references (invalid flows shown with error state in UI)
 */
export const transformTemplateVersion = (
  sourceVersion: CaseTemplateVersionAttributes,
  context: TransformContext,
): Partial<CaseTemplateVersionAttributes> => {
  const isSameWorkspace =
    context.sourceWorkspaceId === context.targetWorkspaceId;

  const result: Record<string, unknown> = { ...sourceVersion };

  for (const field of SYSTEM_FIELDS) {
    delete result[field];
  }

  result.publish_state = "draft";
  result.archived = false;

  if (!isSameWorkspace) {
    result.case_properties = filterCasePropertiesByFeatureGates(
      sourceVersion.case_properties,
      context.targetFeatureGates,
    );
  }

  return result as Partial<CaseTemplateVersionAttributes>;
};
